/**
 * Agent drawer (SPEC §6 item 8): the collapsible bottom drawer that holds
 * everything agent-related — header with tool count, prompt chips, the
 * audition-card slot, the activity ledger (with per-entry Undo closures that
 * the tools wire to `store.undoTo(undoId)`), a visually-hidden
 * `role=status aria-live=polite` line announcing the latest agent action, and
 * a slot for the built-in agent panel.
 *
 *   const drawer = mountAgentDrawer(container, { store });
 *   mountAuditionCard(drawer.el.querySelector('[data-audition-slot]')!, …);
 *   mountAgentPanel(drawer.el.querySelector('[data-agent-panel-slot]')!, …);
 *
 * The open state is remembered in localStorage (`bandmate:drawer`), guarded
 * with try/catch. The drawer auto-opens on any ledger entry with source
 * `'agent'`, on any store change made by the agent (belt and braces for
 * writes that bypass the ledger), and when the audition card dispatches
 * `bm:audition-open`. The status line lives in the always-visible header so
 * it is announced even while the drawer is collapsed (a live region inside a
 * `hidden` body is silent).
 */
import './agent-drawer.css';
import type { Store } from '../state/store-api';
import { getModelContext } from '../webmcp/context';
import { ledger, type LedgerEntry } from '../webmcp/ledger';
import { registry } from '../webmcp/register';
import { h } from './dom';
import { mountLedgerPanel } from './ledger-panel';
import { mountPromptChips } from './prompt-chips';
import { AUDITION_OPEN_EVENT } from './audition-card';

/** localStorage key remembering whether the drawer is open (`'open'` | `'closed'`). */
export const DRAWER_STORAGE_KEY = 'bandmate:drawer';

export interface AgentDrawerDeps {
  /** Subscribed for agent-authored changes (auto-open); the ledger's undo closures call `store.undoTo` themselves. */
  store: Store;
}

export interface AgentDrawerHandle {
  /** Root element (`.bm-drawer`). */
  el: HTMLElement;
  open(): void;
  close(): void;
  isOpen(): boolean;
  dispose(): void;
}

function readStored(): boolean | null {
  try {
    const v = localStorage.getItem(DRAWER_STORAGE_KEY);
    return v === 'open' ? true : v === 'closed' ? false : null;
  } catch {
    return null;
  }
}

function writeStored(open: boolean): void {
  try {
    localStorage.setItem(DRAWER_STORAGE_KEY, open ? 'open' : 'closed');
  } catch {
    /* private mode / quota / blocked — the drawer still works */
  }
}

let seq = 0;

/** Mount the drawer into `container` (typically the bottom of the app column). */
export function mountAgentDrawer(container: HTMLElement, deps: AgentDrawerDeps): AgentDrawerHandle {
  const { store } = deps;
  const bodyId = `bm-drawer-body-${++seq}`;
  let open = false;

  const toolCount = h('span', { class: 'bm-drawer__tools' });
  const toggle = h('button', {
    type: 'button',
    class: 'btn btn--small bm-drawer__toggle',
    'aria-expanded': 'false',
    'aria-controls': bodyId,
  });
  const status = h('p', { class: 'bm-visually-hidden', role: 'status', 'aria-live': 'polite' });
  const head = h(
    'div',
    { class: 'bm-drawer__head' },
    h('h2', { class: 'bm-drawer__title' }, 'Agent'),
    toolCount,
    toggle,
    status,
  );

  const auditionSlot = h('div', { class: 'bm-drawer__audition', dataset: { auditionSlot: '' } });
  const ledgerHost = h('div', { class: 'bm-drawer__ledger' });
  const panelSlot = h('div', { class: 'bm-drawer__panel', dataset: { agentPanelSlot: '' } });
  const chipsHost = h('div', { class: 'bm-drawer__chips' });
  const body = h('div', { class: 'bm-drawer__body', id: bodyId, hidden: true }, chipsHost, auditionSlot, ledgerHost, panelSlot);
  const el = h('section', { class: 'bm-drawer', 'aria-label': 'Agent drawer' }, head, body);

  const chips = mountPromptChips(chipsHost);
  const ledgerPanel = mountLedgerPanel(ledgerHost);

  const setOpen = (next: boolean, persist = true) => {
    open = next;
    body.hidden = !next;
    toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
    toggle.textContent = next ? 'Hide' : 'Show';
    toggle.setAttribute('aria-label', next ? 'Hide agent drawer' : 'Show agent drawer');
    if (next) el.setAttribute('data-open', '');
    else el.removeAttribute('data-open');
    if (persist) writeStored(next);
  };

  toggle.addEventListener('click', () => setOpen(!open));

  const renderTools = () => {
    const n = registry.count();
    toolCount.textContent = `${n} tool${n === 1 ? '' : 's'}`;
  };
  const unsubRegistry = registry.subscribe(renderTools);
  const mc = getModelContext();
  mc?.addEventListener?.('toolchange', renderTools);
  renderTools();

  // Auto-open on agent activity; the first callback only sets the baseline.
  let lastSeenId: string | null | undefined;
  const announce = (e: LedgerEntry | undefined) => {
    if (e) status.textContent = `Agent: ${e.summary}`;
  };
  const unsubLedger = ledger.subscribe((entries) => {
    const latestAgent = entries.find((e) => e.source === 'agent');
    if (lastSeenId === undefined) {
      lastSeenId = latestAgent?.id ?? null;
      announce(latestAgent);
      return;
    }
    if (latestAgent && latestAgent.id !== lastSeenId) {
      lastSeenId = latestAgent.id;
      announce(latestAgent);
      if (!open) setOpen(true);
    }
  });

  const onAuditionOpen = () => {
    if (!open) setOpen(true);
  };
  el.addEventListener(AUDITION_OPEN_EVENT, onAuditionOpen);

  // Agent-authored store changes (mutation or undo) also count as agent activity.
  const unsubStore = store.subscribe((_s, meta) => {
    if (meta.by === 'agent' && (meta.source === 'mutation' || meta.source === 'undo') && !open) setOpen(true);
  });

  setOpen(readStored() ?? false, false);
  container.append(el);

  return {
    el,
    open: () => setOpen(true),
    close: () => setOpen(false),
    isOpen: () => open,
    dispose() {
      unsubRegistry();
      unsubLedger();
      unsubStore();
      mc?.removeEventListener?.('toolchange', renderTools);
      el.removeEventListener(AUDITION_OPEN_EVENT, onAuditionOpen);
      chips.dispose();
      ledgerPanel.dispose();
      el.remove();
    },
  };
}
